import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { SERVICE_OPTIONS, DEFAULT_SERVICE_OPTION } from "../lib/services";
import { Loader2, Mail, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  city: "",
  services: [DEFAULT_SERVICE_OPTION],
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const toggleService = (opt) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(opt)
        ? f.services.filter((s) => s !== opt)
        : [...f.services, opt],
    }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (form.phone.replace(/\D/g, "").length < 7) return "Please enter a valid phone number.";
    if (!form.services.length) return "Please select at least one service.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        service_interest: form.services.join(", "),
        message: form.message,
      });
      setSubmitted(true);
      setForm(EMPTY);
      toast.success("Got it! A ZOZO advisor will reach out within 24 hours.");
    } catch (err) {
      toast.error("Couldn't submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="lead-form" className="bg-white" data-testid="lead-form-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="border-2 border-black bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-5 p-8 sm:p-12 bg-[#F4F4F5] text-black border-b lg:border-b-0 lg:border-r border-black/10">
              <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#EAB308]">
                [ 05 ] Free Consultation
              </p>
              <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-4 leading-tight">
                Kickstart right.
                <br />
                Not just fast.
              </h2>
              <p className="mt-5 text-black/70 leading-relaxed">
                Tell us a bit about yourself and what you need. An advisor will
                reach out within 24 hours with a tailored plan.
              </p>

              <div className="mt-10 space-y-4 text-sm">
                <Contact icon={Mail} label="info@zozoventure.com" testId="contact-email" />
              </div>
            </div>

            <div className="lg:col-span-7 p-8 sm:p-12">
              {submitted ? (
                <SuccessState onReset={() => setSubmitted(false)} />
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="lead-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full name" htmlFor="lead-name">
                      <Input
                        id="lead-name"
                        data-testid="lead-input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Arjun Mehta"
                        className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                      />
                    </Field>
                    <Field label="Phone" htmlFor="lead-phone">
                      <Input
                        id="lead-phone"
                        data-testid="lead-input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+91 98XXXXXX00"
                        className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                      />
                    </Field>
                  </div>

                  <Field label="Email" htmlFor="lead-email">
                    <Input
                      id="lead-email"
                      type="email"
                      data-testid="lead-input-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@company.com"
                      className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                    />
                  </Field>

                  <Field label="City" htmlFor="lead-city">
                    <Input
                      id="lead-city"
                      data-testid="lead-input-city"
                      value={form.city}
                      onChange={update("city")}
                      placeholder="Mumbai, Bengaluru, Delhi NCR…"
                      className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                    />
                  </Field>

                  <Field label="Services of interest (select one or more)" htmlFor="lead-services-group">
                    <div
                      id="lead-services-group"
                      data-testid="lead-input-services"
                      role="group"
                      aria-label="Services of interest"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                    >
                      {[...SERVICE_OPTIONS, "Not sure yet"].map((opt) => {
                        const active = form.services.includes(opt);
                        const slug = opt.toLowerCase().replace(/\s+/g, "-");
                        return (
                          <button
                            type="button"
                            key={opt}
                            data-testid={`service-option-${slug}`}
                            aria-pressed={active}
                            onClick={() => toggleService(opt)}
                            className={`flex items-center gap-3 border px-3.5 py-3 text-left text-sm transition-colors ${
                              active
                                ? "border-[#EAB308] bg-[#EAB308] text-white"
                                : "border-black/25 bg-white text-black hover:border-black"
                            }`}
                          >
                            <span
                              className={`inline-flex w-5 h-5 items-center justify-center border shrink-0 ${
                                active
                                  ? "border-white bg-white"
                                  : "border-black/40 bg-white"
                              }`}
                            >
                              {active && <Check size={14} strokeWidth={3} className="text-[#EAB308]" />}
                            </span>
                            <span className="font-medium">{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                    {form.services.length > 0 && (
                      <p className="mt-2 text-xs text-black/60" data-testid="services-selected-count">
                        {form.services.length} selected
                      </p>
                    )}
                  </Field>

                  <Field label="Tell us more (optional)" htmlFor="lead-message">
                    <Textarea
                      id="lead-message"
                      data-testid="lead-input-message"
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Briefly describe your business and what you need help with…"
                      className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-submit-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#EAB308] text-white px-6 py-4 font-semibold hover:bg-black transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} /> Submitting…
                      </>
                    ) : (
                      <>Request free consultation →</>
                    )}
                  </button>

                  <ul
                    className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] font-medium text-black/70"
                    data-testid="lead-form-reassurance"
                  >
                    <li className="inline-flex items-center gap-1">
                      <Check size={12} className="text-[#EAB308]" strokeWidth={3} />
                      100% Free
                    </li>
                    <li className="inline-flex items-center gap-1">
                      <Check size={12} className="text-[#EAB308]" strokeWidth={3} />
                      No commitment
                    </li>
                    <li className="inline-flex items-center gap-1">
                      <Check size={12} className="text-[#EAB308]" strokeWidth={3} />
                      Reply within 2 hours
                    </li>
                    <li className="inline-flex items-center gap-1">
                      <Check size={12} className="text-[#EAB308]" strokeWidth={3} />
                      No spam, ever
                    </li>
                  </ul>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-xs font-semibold tracking-[0.15em] uppercase text-black/70 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function Contact({ icon: Icon, label, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span className="inline-flex w-9 h-9 items-center justify-center border border-black/25">
        <Icon size={16} />
      </span>
      <span className="text-black/85">{label}</span>
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <div className="h-full flex flex-col items-start justify-center py-6" data-testid="lead-form-success">
      <div className="w-12 h-12 bg-[#EAB308] flex items-center justify-center text-white font-heading font-black text-2xl">
        ✓
      </div>
      <h3 className="font-heading font-black text-3xl sm:text-4xl mt-5">
        We&apos;ve got your request.
      </h3>
      <p className="mt-3 text-black/70 max-w-md">
        A ZOZO advisor will reach out within 24 hours with a tailored plan for
        your Startup. Meanwhile, check your inbox for a quick starter guide.
      </p>
      <button
        onClick={onReset}
        data-testid="lead-form-reset"
        className="mt-6 border border-black px-5 py-3 font-semibold hover:bg-black hover:text-white transition-colors"
      >
        Submit another inquiry
      </button>
    </div>
  );
}
